import React, { useState, useEffect } from "react";
import {
  getFunctions,
  getGrades,
  getZones,
  getJobLocations,
  getEmployeeDetails,
  getDepartments,
  getRoleType,
  getJobCategory,
  getEmployeeSuggestions,
  getGenderType,
} from "../../../../Auth/Services/metadataService";
import {
  getJobDetails,
  updateJobOpening,
  getJobInterviewers,
  deleteJobInterviewer,
} from "../Services/DraftJobService";
import NormalAlert from "../../../../Alerts/NormalAlert";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import AddInterviewerModal from "../../PostJob/Modal/AddInterviewerModal";
import bgImage from "../../../../../Assets/bg3.jpg";
import { Trash2, Eye } from "lucide-react";
import ConfirmationAlert from "../../../../Alerts/ConfirmationAlert";
import JobDescriptionModal from "../Modals/JobDescriptionModal";
import { useLocation } from "react-router-dom";

// Author: Harshada Bhavsar (18/7/2025)
// API: Create Job Opening for Employee
const UpdatePostJobForm = ({ onSubmit, onCancel }) => {
  const { jobUID } = useParams();
  const [formData, setFormData] = useState({
    jobTitle: "",
    reportingManagerID: "",
    reportingManager: "",
    functionalManagerID: "",
    functionalManager: "",
    function: "",
    functionID: 0,
    grade: "",
    roleName: "",
    jobCategory: "",
    zone: "",
    jobLocation: "",
    offeredSalary: "",
    minimumQualification: "",
    minimumExperience: "",
    maximumExperience: "",
    genderPreference: "",
    positionType: "",
    requisitionDate: "",
    jobDescription: "",
    department: "",
    numberOfOpenings: 0,
    roleType: "",
    jobInterviewers: [],
    replacedEmployeeName: "",
    replacedEmployeeID: "",
    Remark: "",
  });

  const [functions, setFunctions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [zones, setZones] = useState([]);
  const [jobLocations, setJobLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roleType, setRoleType] = useState([]);
  const [jobCategory, setJobCategory] = useState([]);
  const [alert, setAlert] = useState(null);
  const [confirmAlert, setConfirmAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newInterviewer, setNewInterviewer] = useState({
    interviewerCode: "",
    interviewerName: "",
    interviewSequence: 1,
  });
  const [modalInterviewers, setModalInterviewers] = useState([]);
  const [activeField, setActiveField] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isDeptMapped, setIsDeptMapped] = useState(false);
  const [genderType, setGenderType] = useState([]);
  const [showJDModal, setShowJDModal] = useState(false);
  const location = useLocation();
  const readOnlyMode = location.state?.readOnlyMode || false;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobUID) return;

      try {
        const res = await getJobDetails(jobUID);
        const job = res?.data?.jobData?.[0];
        if (!job) return;

        const interviewerRes = await getJobInterviewers(jobUID);
        const interviewerData = interviewerRes?.data?.interviewerData || [];

        setFormData({
          jobUID: job.jobUID,
          roleName: job.jobTitle || "",
          reportingManagerID: job.reportingManagerID || "",
          reportingManager: job.reportingManageraName
            ? `${job.reportingManagerID} - ${job.reportingManageraName}`
            : "",
          functionalManagerID: job.functionalManagerID || "",
          functionalManager: job.functionalManageraName
            ? `${job.functionalManagerID} - ${job.functionalManageraName}`
            : "",
          function: job.function || "",
          department: job.department || "",
          grade: job.grade || "",
          zone: job.zone || "",
          jobLocation: job.location || "",
          offeredSalary: job.offeredSalary || "",
          minimumQualification: job.minimumQualification || "",
          minimumExperience: job.minimumExperience || "",
          maximumExperience: job.maximumExperience || "",
          genderPreference: job.genderPreference || "",
          positionType: job.positionType || "",
          requisitionDate: job.requisitionDate
            ? formatDate(job.requisitionDate)
            : "",
          jobDescription: job.jobDescription || "",
          numberOfOpenings: job.numberOfOpenings || "",
          jobCategory: job.jobCategory || "",
          roleType: job.roleType || "",
          Remark: job.remark || "",
          replacedEmployeeID: job.replacedEmployee || "",
          replacedEmployeeName: job.replacedEmployeeName
            ? `${job.replacedEmployee} - ${job.replacedEmployeeName}`
            : "",
          jobInterviewers: interviewerData.map((intv, index) => ({
            interviewerCode: intv.interviewerCode,
            interviewerName: intv.interviewerName || "",
            interviewerDisplay: intv.interviewerName
              ? `${intv.interviewerCode} - ${intv.interviewerName}`
              : intv.interviewerCode,
            interviewSequence: Number(intv.interviewerSequence) || index + 1,
          })),
        });
      } catch (err) {
        setAlert({
          type: "fail",
          message: "Failed to load job details.",
        });
      }
    };

    const formatDate = (inputDate) => {
      if (!inputDate) return "";

      try {
        return inputDate.split("T")[0];
      } catch {
        return "";
      }
    };

    fetchJobDetails();
  }, [jobUID]);

  useEffect(() => {
    const mapApiValuesToDropdownIds = () => {
      if (
        !functions.length ||
        !zones.length ||
        !jobCategory.length ||
        !roleType.length
      )
        return;

      setFormData((prev) => {
        const updated = { ...prev };
        const safeLower = (val) =>
          typeof val === "string" ? val.toLowerCase().trim() : "";

        //  Match Function (convert function name -> ID)
        const matchedFunction = functions.find(
          (f) => safeLower(f.functionName) === safeLower(prev.function),
        );
        if (matchedFunction) updated.function = matchedFunction.functionID;

        //  Match Zone
        const matchedZone = zones.find(
          (z) => safeLower(z.zoneName) === safeLower(prev.zone),
        );
        if (matchedZone) updated.zone = matchedZone.zoneID;

        //  Match Job Location
        const matchedLocation = jobLocations.find(
          (l) => safeLower(l.jobLocation) === safeLower(prev.jobLocation),
        );
        if (matchedLocation) updated.jobLocation = matchedLocation.locationID;

        //  Match Job Category
        const matchedCategory = jobCategory.find(
          (c) => safeLower(c.jobCategory) === safeLower(prev.jobCategory),
        );
        if (matchedCategory)
          updated.jobCategory = matchedCategory.jobCategoryID;

        //  Match Role Type
        const matchedRoleType = roleType.find(
          (r) => safeLower(r.roleType) === safeLower(prev.roleType),
        );
        if (matchedRoleType) updated.roleType = matchedRoleType.roleTypeID;

        return updated;
      });
    };

    mapApiValuesToDropdownIds();
  }, [functions, zones, jobLocations, jobCategory, roleType]);

  useEffect(() => {
    if (
      departments.length > 0 &&
      typeof formData.department === "string" &&
      !isDeptMapped
    ) {
      const safeLower = (val) =>
        typeof val === "string" ? val.toLowerCase().trim() : "";

      const matchedDept = departments.find(
        (d) => safeLower(d.departmentName) === safeLower(formData.department),
      );

      if (matchedDept) {
        setFormData((prev) => ({
          ...prev,
          department: matchedDept.departmentID,
        }));

        setIsDeptMapped(true);
      }
    }
  }, [departments, formData.department]);

  useEffect(() => {
    const fetchData = async () => {
      setFunctions(await getFunctions());
      setGrades(await getGrades());
      setZones(await getZones());
      setRoleType(await getRoleType());
      setJobCategory(await getJobCategory());
      setGenderType(await getGenderType());
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.zone) {
      getJobLocations(formData.zone).then(setJobLocations);
    } else {
      setJobLocations([]);
    }
  }, [formData.zone]);

  // Open Modal
  const handleOpenModal = () => {
    setNewInterviewer({
      interviewerCode: "",
      interviewerName: "",
      interviewSequence: (formData.jobInterviewers.length || 0) + 1,
    });
    setShowModal(true);
  };

  // Save interviewers from modal → main form state
  const handleSaveInterviewers = () => {
    setFormData((prev) => ({
      ...prev,
      jobInterviewers: [...prev.jobInterviewers, ...modalInterviewers],
    }));
    setShowModal(false);
  };

  //Department Dropdown
  useEffect(() => {
    const fetchDepartments = async () => {
      if (formData.function) {
        const result = await getDepartments(formData.function);
        setDepartments(result || []);
        if (isDeptMapped) {
          setFormData((prev) => ({
            ...prev,
            department: "",
          }));
          setIsDeptMapped(false);
        }
      }
    };

    fetchDepartments();
  }, [formData.function]);

  const handleChange = async (e, fieldName) => {
    if (typeof e === "string" && fieldName) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: e,
      }));
    } else {
      const { name, value } = e.target;
      let newValue = value;

      if (
        [
          "numberOfOpenings",
          "minimumExperience",
          "offeredSalary",
          "maximumExperience",
        ].includes(name) &&
        Number(value) < 0
      ) {
        newValue = "1";
      }

      // If it's interviewerCode, update newInterviewer instead of formData
      if (name === "interviewerCode") {
        setNewInterviewer((prev) => ({
          ...prev,
          interviewerCode: newValue,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }

      //  Handle autocomplete for interviewerCode as well
      if (
        (name === "reportingManager" ||
          name === "functionalManager" ||
          name === "interviewerCode" ||
          name === "replacedEmployeeName") &&
        value.trim().length >= 3
      ) {
        try {
          const res = await getEmployeeSuggestions(value.trim());
          if (res && res.length > 0) {
            setActiveField(name);
            setSuggestions(res);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch (err) {
          const safeMessage =
            err.response?.data?.message ||
            "Something went wrong. Please try again.";

          setAlert({
            type: "fail",
            message: safeMessage,
          });
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    if (activeField === "reportingManager") {
      setFormData((prev) => ({
        ...prev,
        reportingManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
        reportingManagerID: suggestion.employeeCode,
      }));
    } else if (activeField === "functionalManager") {
      setFormData((prev) => ({
        ...prev,
        functionalManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
        functionalManagerID: suggestion.employeeCode,
      }));
    } else if (activeField === "interviewerCode") {
      setNewInterviewer((prev) => ({
        ...prev,
        interviewerCode: suggestion.employeeCode,
        interviewerName: suggestion.employeeName,
        interviewerDisplay: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
      }));
    } else if (activeField === "replacedEmployeeName") {
      setFormData((prev) => ({
        ...prev,
        replacedEmployeeName: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
        replacedEmployeeID: suggestion.employeeCode,
      }));
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const isEmpty = (value) =>
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "");

    if (isEmpty(formData.roleName))
      newErrors.roleName = "Job Title is required";
    if (isEmpty(formData.reportingManagerID))
      newErrors.reportingManagerID = "Reporting Manager ID is required";
    if (isEmpty(formData.functionalManagerID))
      newErrors.functionalManagerID = "Zonal Manager ID is required";
    if (isEmpty(formData.function)) newErrors.function = "Function is required";
    if (isEmpty(formData.grade)) newErrors.grade = "Grade is required";
    if (isEmpty(formData.zone)) newErrors.zone = "Zone is required";
    if (isEmpty(formData.jobLocation))
      newErrors.jobLocation = "Job Location is required";
    if (isEmpty(formData.offeredSalary))
      newErrors.offeredSalary = "Offered Salary is required";
    if (isEmpty(formData.minimumQualification))
      newErrors.minimumQualification = "Minimum Qualification is required";
    if (isEmpty(formData.minimumExperience))
      newErrors.minimumExperience = "Minimum Experience is required";
    if (isEmpty(formData.maximumExperience))
      newErrors.maximumExperience = "Maximum Experience is required";
    if (isEmpty(formData.genderPreference))
      newErrors.genderPreference = "Gender Preference is required";
    if (isEmpty(formData.positionType))
      newErrors.positionType = "Position Type is required";
    if (isEmpty(formData.requisitionDate))
      newErrors.requisitionDate = "Requisition Date is required";
    if (
      !formData.jobDescription ||
      (typeof formData.jobDescription !== "string" &&
        !(formData.jobDescription instanceof File))
    ) {
      newErrors.jobDescription = "Job Description is required";
    }
    if (isEmpty(formData.department))
      newErrors.department = "Department is required";
    if (isEmpty(formData.roleType))
      newErrors.roleType = "Role Type is required";
    // if (!formData.jobInterviewers || formData.jobInterviewers.length === 0) {
    //   newErrors.jobInterviewers = "At least one interviewer is required";
    // } else {
    //   formData.jobInterviewers.forEach((i, idx) => {
    //     if (!i.interviewerCode) {
    //       newErrors[`jobInterviewers_${idx}_code`] =
    //         "Interviewer Code is required";
    //     }
    //     if (!i.interviewSequence || i.interviewSequence <= 0) {
    //       newErrors[`jobInterviewers_${idx}_seq`] =
    //         "Valid Interview Sequence is required";
    //     }
    //   });
    // }

    if (
      formData.numberOfOpenings === undefined ||
      formData.numberOfOpenings === null ||
      formData.numberOfOpenings === "" ||
      formData.numberOfOpenings === 0
    ) {
      newErrors.numberOfOpenings = "Number of Openings is required";
    }
    if (isEmpty(formData.jobCategory))
      newErrors.jobCategory = "Job Category is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        jobUID: formData.jobUID,
        jobTitle: formData.roleName,
        reportingManagerID: formData.reportingManagerID,
        functionalManagerID: formData.functionalManagerID,
        function: String(formData.function),
        department: String(formData.department),
        grade: String(formData.grade),
        zone: String(formData.zone),
        location: String(formData.jobLocation),
        offeredSalary: formData.offeredSalary,
        minimumQualification: formData.minimumQualification,
        minimumExperience: formData.minimumExperience,
        maximumExperience: formData.maximumExperience,
        genderPreference: formData.genderPreference,
        positionType: formData.positionType,
        requisitionDate: formData.requisitionDate,
        jobDescription:
          formData.jobDescription instanceof File
            ? formData.jobDescription
            : null,

        ExistingJobDescription:
          typeof formData.jobDescription === "string"
            ? formData.jobDescription
            : "",
        numberOfOpenings: Number(formData.numberOfOpenings),
        jobCategory: Number(formData.jobCategory),
        roleType: Number(formData.roleType),
        jobInterviewers: formData.jobInterviewers,
        replacedEmployee: formData.replacedEmployeeID,
        remark: formData.Remark,
      };

      const clientErrors = validateForm();
      if (Object.keys(clientErrors).length > 0) {
        setErrors(clientErrors);
        return;
      }

      const response = await updateJobOpening({
        ...payload,
        jobUID: formData.jobUID,
      });
      setAlert({ type: response.data.status, message: response.data.message });
      if (onSubmit) onSubmit(response?.data || payload);
      setTimeout(() => {
        navigate("/Dashboard/Draft-Jobs");
      }, 1500);
    } catch (error) {
      setAlert({ type: "fail", message: error.response.data.message });
    }
  };

  const handleReset = () => {
    setFormData({
      jobTitle: "",
      reportingManagerID: "",
      reportingManager: "",
      functionalManagerID: "",
      functionalManager: "",
      function: "",
      functionID: 0,
      grade: "",
      roleName: "",
      jobCategory: "",
      zone: "",
      jobLocation: "",
      offeredSalary: "",
      minimumQualification: "",
      minimumExperience: "",
      maximumExperience: "",
      genderPreference: "",
      positionType: "",
      requisitionDate: "",
      jobDescription: "",
      department: "",
      numberOfOpenings: 0,
      roleType: "",
      jobInterviewers: [],
    });
    setErrors({});
    setError("");
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.jobInterviewers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Reassign interviewSequence
    const updatedItems = items.map((item, index) => ({
      ...item,
      interviewSequence: index + 1, // Round numbers start from 1
    }));

    setFormData((prev) => ({
      ...prev,
      jobInterviewers: updatedItems,
    }));
  };

  return (
    <>
      <div className="min-h-screen bg-[#f4f6f9]">
        {/* Top Banner */}
        <div
          className="w-full h-[200px] bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        {/* Form Wrapper */}
        <div className="flex justify-center -mt-20 pb-20 px-4">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-center text-2xl font-semibold text-primary mb-8">
              UPDATE JOB ROLE
            </h3>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/*  JOB DETAILS  */}
              <div className="border rounded-lg">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Job Details
                </div>

                <div className="p-6 space-y-6">
                  {/* Job Title */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Profile <span className="text-red-500">*</span>
                    </label>

                    <input
                      name="roleName"
                      value={formData.roleName || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.roleName ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />

                    {errors.roleName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.roleName}
                      </p>
                    )}
                  </div>

                  {/* Job Description */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Upload Job Description (PDF Only)
                      <span className="text-red-500"> *</span>
                    </label>

                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="application/pdf"
                        disabled={readOnlyMode}
                        className={`w-full border rounded-md px-3 py-2 bg-white ${
                          errors.jobDescription
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const MAX_FILE_SIZE = 5 * 1024 * 1024;

                          if (file) {
                            if (file.type !== "application/pdf") {
                              setErrors((prev) => ({
                                ...prev,
                                jobDescription: "Only PDF files are allowed",
                              }));
                              return;
                            }

                            if (file.size > MAX_FILE_SIZE) {
                              setAlert({
                                type: "fail",
                                message:
                                  "Selected file exceeds the 5MB limit. Please upload a smaller file.",
                              });

                              setErrors((prev) => ({
                                ...prev,
                                jobDescription: "File size must be < 5MB",
                              }));

                              e.target.value = "";
                              return;
                            }

                            setFormData((prev) => ({
                              ...prev,
                              jobDescription: file,
                            }));

                            setErrors((prev) => ({
                              ...prev,
                              jobDescription: "",
                            }));
                          }
                        }}
                      />

                      {/* View Current JD Button */}
                      {typeof formData.jobDescription === "string" &&
                        formData.jobDescription && (
                          <button
                            type="button"
                            onClick={() => setShowJDModal(true)}
                            className="flex items-center gap-1 border border-blue-500 text-blue-500 px-3 py-2 rounded-md hover:bg-blue-50 whitespace-nowrap"
                          >
                            <Eye size={16} />
                            View
                          </button>
                        )}
                    </div>

                    {errors.jobDescription && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.jobDescription}
                      </p>
                    )}

                    {/* JD Modal */}
                    {showJDModal && (
                      <JobDescriptionModal
                        show={showJDModal}
                        onHide={() => setShowJDModal(false)}
                        fullPath={formData.jobDescription}
                      />
                    )}
                  </div>

                  {/* Category + Position */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Job Category <span className="text-red-500">*</span>
                      </label>

                      <select
                        name="jobCategory"
                        value={formData.jobCategory || ""}
                        onChange={handleChange}
                        disabled={readOnlyMode}
                        className={`w-full border ${
                          errors.jobCategory
                            ? "border-red-500"
                            : "border-gray-300"
                        } rounded-md px-3 py-2  focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="">Select</option>
                        {jobCategory.map((j, index) => (
                          <option key={index} value={j.jobCategoryID}>
                            {j.jobCategory}
                          </option>
                        ))}
                      </select>

                      {errors.jobCategory && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.jobCategory}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Position Type <span className="text-red-500">*</span>
                      </label>

                      <select
                        name="positionType"
                        value={formData.positionType || ""}
                        onChange={handleChange}
                        disabled={readOnlyMode}
                        className={`w-full border ${
                          errors.positionType
                            ? "border-red-500"
                            : "border-gray-300"
                        } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="">Select</option>
                        <option value="Replacement">Replacement</option>
                        <option value="New_Opening">New Opening</option>
                      </select>

                      {errors.positionType && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.positionType}
                        </p>
                      )}
                    </div>

                    {formData.positionType === "Replacement" && (
                      <div className="relative">
                        <label className="block text-sm font-medium mb-1">
                          Replaced Employee{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        <input
                          name="replacedEmployeeName"
                          value={formData.replacedEmployeeName || ""}
                          onChange={handleChange}
                          disabled={readOnlyMode}
                          onBlur={() =>
                            setTimeout(() => setShowSuggestions(false), 200)
                          }
                          onKeyDown={handleKeyDown}
                          autoComplete="off"
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />

                        {showSuggestions &&
                          activeField === "replacedEmployeeName" && (
                            <ul className="absolute left-0 right-0 mt-1 bg-white border rounded shadow max-h-48 overflow-y-auto">
                              {suggestions.map((s, idx) => (
                                <li
                                  key={idx}
                                  onClick={() => handleSelectSuggestion(s)}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                >
                                  {s.employeeCode} - {s.employeeName}
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    )}

                    {/* Requisition Date */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Requisition Date <span className="text-red-500">*</span>
                      </label>

                      <input
                        type="date"
                        name="requisitionDate"
                        value={formData.requisitionDate || ""}
                        onChange={handleChange}
                        max={new Date().toISOString().split("T")[0]}
                        className={`w-full border ${
                          errors.requisitionDate
                            ? "border-red-500"
                            : "border-gray-300"
                        } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />

                      {errors.requisitionDate && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.requisitionDate}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/*  ORGANIZATIONAL INFORMATION  */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Organizational Information
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Department (Function) */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Function <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="function"
                      value={formData.function || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.function ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {functions.map((f, index) => (
                        <option key={index} value={f.functionID}>
                          {f.functionName}
                        </option>
                      ))}
                    </select>
                    {errors.function && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.function}
                      </p>
                    )}
                  </div>

                  {/* Zonal Manager */}
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">
                      Functional Manager / Co-operate Manager{" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      name="functionalManager"
                      value={formData.functionalManager || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      onBlur={() =>
                        setTimeout(() => setShowSuggestions(false), 200)
                      }
                      onKeyDown={(e) => handleKeyDown(e)}
                      autoComplete="off"
                      className={`w-full border ${
                        errors.functionalManagerID
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />

                    {errors.functionalManagerID && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.functionalManagerID}
                      </p>
                    )}

                    {/*  Suggestions Dropdown */}
                    {showSuggestions &&
                      activeField === "functionalManager" &&
                      suggestions.length > 0 && (
                        <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
                          {suggestions.map((s, idx) => (
                            <li
                              key={idx}
                              onClick={() => handleSelectSuggestion(s)}
                              className={`px-3 py-2 cursor-pointer ${
                                highlightedIndex === idx
                                  ? "bg-blue-500 text-white"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              {s.employeeCode} - {s.employeeName}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>

                  {/* Profile (Department) */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="department"
                      value={formData.department || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.department ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept.departmentID}>
                          {dept.departmentName}
                        </option>
                      ))}
                    </select>
                    {errors.department && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.department}
                      </p>
                    )}
                  </div>

                  {/* Reporting Manager */}
                  <div className="relative">
                    <label className="block text-sm font-medium mb-1">
                      Reporting Manager <span className="text-red-500">*</span>
                    </label>

                    <input
                      name="reportingManager"
                      value={formData.reportingManager || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      onBlur={() =>
                        setTimeout(() => setShowSuggestions(false), 200)
                      }
                      onKeyDown={(e) => handleKeyDown(e)}
                      autoComplete="off"
                      className={`w-full border ${
                        errors.reportingManagerID
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />

                    {errors.reportingManagerID && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.reportingManagerID}
                      </p>
                    )}

                    {/*  Suggestions Dropdown */}
                    {showSuggestions &&
                      activeField === "reportingManager" &&
                      suggestions.length > 0 && (
                        <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
                          {suggestions.map((s, idx) => (
                            <li
                              key={idx}
                              onClick={() => handleSelectSuggestion(s)}
                              className={`px-3 py-2 cursor-pointer ${
                                highlightedIndex === idx
                                  ? "bg-blue-500 text-white"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              {s.employeeCode} - {s.employeeName}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>

                  {/* Grade */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Grade <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="grade"
                      value={formData.grade || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.grade ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {grades.map((g, index) => (
                        <option key={index} value={g.grade}>
                          {g.gradeName}
                        </option>
                      ))}
                    </select>
                    {errors.grade && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.grade}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/*  LOCATION DETAILS  */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Location Details
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Zone */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Zone <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="zone"
                      value={formData.zone || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.zone ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {zones.map((z, index) => (
                        <option key={index} value={z.zoneID}>
                          {z.zoneName}
                        </option>
                      ))}
                    </select>
                    {errors.zone && (
                      <p className="text-red-500 text-sm mt-1">{errors.zone}</p>
                    )}
                  </div>

                  {/* Job Location */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Job Location <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="jobLocation"
                      value={formData.jobLocation || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.jobLocation
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {jobLocations.map((j, index) => (
                        <option key={index} value={j.locationID}>
                          {j.jobLocation}
                        </option>
                      ))}
                    </select>
                    {errors.jobLocation && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.jobLocation}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/*  CANDIDATE REQUIREMENTS  */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Candidate Requirements
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Minimum Qualification */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Minimum Qualification{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="minimumQualification"
                      value={formData.minimumQualification || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.minimumQualification
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.minimumQualification && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.minimumQualification}
                      </p>
                    )}
                  </div>

                  {/* Minimum Experience */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Minimum Experience (Years){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="minimumExperience"
                      value={formData.minimumExperience || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.minimumExperience
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.minimumExperience && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.minimumExperience}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Maximum Experience (Years){" "}
                      <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="number"
                      name="maximumExperience"
                      value={formData.maximumExperience || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.maximumExperience
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />

                    {errors.maximumExperience && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.maximumExperience}
                      </p>
                    )}
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Gender Preference <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="genderPreference"
                      value={formData.genderPreference || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.genderPreference
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {genderType.map((g, index) => (
                        <option key={index} value={g.genderType}>
                          {g.genderType}
                        </option>
                      ))}
                    </select>
                    {errors.genderPreference && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.genderPreference}
                      </p>
                    )}
                  </div>

                  {/* Role Type */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Role Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="roleType"
                      value={formData.roleType || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.roleType ? "border-red-500" : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">Select</option>
                      {roleType.map((r, index) => (
                        <option key={index} value={r.roleTypeID}>
                          {r.roleType}
                        </option>
                      ))}
                    </select>
                    {errors.roleType && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.roleType}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Remarks / Specifications
                    </label>

                    <ReactQuill
                      theme="snow"
                      value={formData.Remark || ""}
                      onChange={(content) => handleChange(content, "Remark")}
                      readOnly={readOnlyMode}
                      className="bg-white rounded-md"
                      style={{ height: "200px" }}
                    />

                    {errors.Remark && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.Remark}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/*  COMPENSATION & OPENINGS  */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Compensation & Openings
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Number of Openings */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Number of Openings <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="numberOfOpenings"
                      value={formData.numberOfOpenings || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.numberOfOpenings
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.numberOfOpenings && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.numberOfOpenings}
                      </p>
                    )}
                  </div>

                  {/* Maximum Budget */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Maximum Budget <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="offeredSalary"
                      value={formData.offeredSalary || ""}
                      onChange={handleChange}
                      disabled={readOnlyMode}
                      className={`w-full border ${
                        errors.offeredSalary
                          ? "border-red-500"
                          : "border-gray-300"
                      } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                    {errors.offeredSalary && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.offeredSalary}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/*  INTERVIEW PANEL  */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-master-header text-white px-4 py-3 font-medium">
                  Interview Panel
                </div>

                <div className="p-6 space-y-4">
                  <button
                    type="button"
                    onClick={handleOpenModal}
                    disabled={readOnlyMode}
                    className="bg-primary text-white px-4 py-2 rounded-md transition"
                  >
                    ➕ Add Interviewer
                  </button>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="interviewers-list">
                      {(provided) => (
                        <ul
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-2"
                        >
                          {formData.jobInterviewers.map((intv, index) => (
                            <Draggable
                              key={intv.interviewerCode.toString()}
                              draggableId={intv.interviewerCode.toString()}
                              index={index}
                            >
                              {(provided) => (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="flex justify-between items-center border rounded-md px-4 py-2 bg-gray-50"
                                >
                                  <span>
                                    {intv.interviewerDisplay ||
                                      `${intv.interviewerCode} - ${intv.interviewerName}`}{" "}
                                    (Round: {intv.interviewSequence})
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setConfirmAlert({
                                        title: "Delete Interviewer",
                                        message: `Delete interviewer ${intv.interviewerDisplay}?`,
                                        onConfirm: async () => {
                                          await deleteJobInterviewer(
                                            formData.jobUID,
                                            intv.interviewerCode,
                                          );

                                          setFormData((prev) => ({
                                            ...prev,
                                            jobInterviewers:
                                              prev.jobInterviewers.filter(
                                                (i) =>
                                                  i.interviewerCode !==
                                                  intv.interviewerCode,
                                              ),
                                          }));

                                          setConfirmAlert(null);
                                        },
                                        onCancel: () => setConfirmAlert(null),
                                      });
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {formData.jobInterviewers.length === 0 && (
                    <p className="text-center text-gray-500">
                      No interviewers added yet.
                    </p>
                  )}
                </div>
              </div>

              {/*  BUTTONS  */}
              <div className="flex justify-center gap-6 pt-6">
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-2 rounded-md hover:bg-[#174d91] transition"
                >
                  UPDATE ROLE
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className="border border-gray-400 text-gray-700 px-8 py-2 rounded-md hover:bg-gray-100 transition"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <AddInterviewerModal
        show={showModal}
        onClose={() => setShowModal(false)}
        newInterviewer={newInterviewer}
        setNewInterviewer={setNewInterviewer}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        handleSelectSuggestion={handleSelectSuggestion}
        showSuggestions={showSuggestions}
        activeField={activeField}
        suggestions={suggestions}
        highlightedIndex={highlightedIndex}
        setFormData={setFormData}
        formData={formData}
        setShowSuggestions={setShowSuggestions}
      />

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {confirmAlert && (
        <ConfirmationAlert
          title={confirmAlert.title}
          message={confirmAlert.message}
          onConfirm={confirmAlert.onConfirm}
          onCancel={confirmAlert.onCancel}
        />
      )}
    </>
  );
};

export default UpdatePostJobForm;
